import { type ReactElement, useCallback, useEffect, useState } from 'react';
import {
  type BlockItemView,
  type OccurrenceAudienceView,
  type OccurrenceReasonView,
  type OccurrenceStatusView,
  type OccurrenceView,
  type RecentRatingView,
  type RelationshipsOverviewView,
} from '@vello/api-client';
import { Badge, Button, CheckRow, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyState, Field, Notice, PageHeader, Panel, SegmentedControl, Select, Table, TextArea, TextInput, FormRow, SectionTitle, FormGrid, Cluster, Stack, Text, CounterRow, PreWrap } from '@vello/web-theme';
import {
  OCCURRENCE_STATUS_TONE,
  countryOfMarketId,
  formatMarketInstant,
  marketTimeZone,
  occurrenceReasonLabel,
  reputationAuthority,
  tailOf,
} from '../../src/index';
import { PersonName, usePersonName } from '../components/person-name';
import { DrawerStack } from '../components/drawer-stack';
import { MarketField } from '../components/market-field';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

type Tab = 'occurrences' | 'ratings' | 'blocks' | 'reasons';
const STATUSES: readonly ('' | OccurrenceStatusView)[] = ['', 'NEW', 'ANSWERED', 'CLOSED'];
const AUDIENCES: readonly OccurrenceAudienceView[] = ['BOTH', 'RIDER', 'DRIVER'];

/**
 * **Operações › Relacionamentos** (W9-REPUTATION, D478, ADR-0096; DevMobility I-031 "Gestão de relacionamentos: ocorrências,
 * avaliações, blacklist, contadores", D-052) over `GET /v1/ops/relationships/overview | blocks`, `GET /v1/ops/occurrences`,
 * `POST …/:id/answer | close`, `GET /v1/ops/ratings/recent` and the catalogue of reasons `GET/PUT /v1/ops/occurrence-reasons`.
 *
 * - The counters on top: the market's occurrences by status, and the country's blocks (with how many are safety blocks) and favorites.
 * - **Ocorrências**: what riders and drivers reported about a ride, newest first; open one to read it, answer it (the person reads the
 *   answer in his app) and close it. **Avaliações**: the low ratings with the tags and the reason the rider gave. **Bloqueios**: the
 *   blacklist — who blocked whom and why. **Motivos**: the catalogue riders and drivers pick the reason from, per country.
 * - One primary action at a time ("Responder", or "Salvar motivo" on its tab). The market comes from the operator's scope; a refused
 *   read is an error, never an empty list. Reading a report needs the `occurrence:read` word, answering `occurrence:respond`.
 */
export function RelationshipsScreen(): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { t, lang } = useT();
  const { v, language, uncatalogued, explain } = useVocab();
  const authority = reputationAuthority(grants);
  const [market, setMarket] = useState('');
  const [tab, setTab] = useState<Tab>('occurrences');
  const [overview, setOverview] = useState<RelationshipsOverviewView | undefined>(undefined);
  const [reasons, setReasons] = useState<readonly OccurrenceReasonView[]>([]);
  const [status, setStatus] = useState<'' | OccurrenceStatusView>('NEW');
  const [occurrences, setOccurrences] = useState<readonly OccurrenceView[] | undefined>(undefined);
  const [selected, setSelected] = useState<OccurrenceView | undefined>(undefined);
  const [answer, setAnswer] = useState('');
  const [maxStars, setMaxStars] = useState(2);
  const [ratings, setRatings] = useState<readonly RecentRatingView[] | undefined>(undefined);
  const [blockReason, setBlockReason] = useState<'' | 'safety' | 'preference'>('');
  const [blocks, setBlocks] = useState<readonly BlockItemView[] | undefined>(undefined);
  const [unblocking, setUnblocking] = useState<BlockItemView | undefined>(undefined);
  const [unblockReason, setUnblockReason] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [done, setDone] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ code: '', audience: 'BOTH' as OccurrenceAudienceView, pt: '', en: '', order: '100', active: true });

  const tz = marketTimeZone(market === '' ? undefined : market, markets);
  const country = market === '' ? undefined : countryOfMarketId(market);
  const fail = useCallback((e: { readonly reasonCode?: string; readonly code: string }): void => setError(explain(e) ?? v('api.error_code', e.code)), [explain, v]);

  const loadOverview = useCallback(async (): Promise<void> => {
    if (market === '' || !authority.mayViewRelationships) { setOverview(undefined); return; }
    const res = await runtime.reputation.overview({ marketId: market });
    if (res.ok) setOverview(res.value); else fail(res.error);
  }, [runtime, market, authority.mayViewRelationships, fail]);

  const loadReasons = useCallback(async (): Promise<void> => {
    if (country === undefined || !authority.mayReadOccurrences) { setReasons([]); return; }
    const res = await runtime.reputation.occurrenceReasons(country);
    if (res.ok) setReasons(res.value.reasons); else fail(res.error);
  }, [runtime, country, authority.mayReadOccurrences, fail]);

  const loadTab = useCallback(async (): Promise<void> => {
    setError(undefined);
    if (market === '' || country === undefined) return;
    if (tab === 'occurrences' && authority.mayReadOccurrences) {
      setOccurrences(undefined);
      const res = await runtime.reputation.occurrences({ marketId: market, ...(status !== '' ? { status } : {}), limit: 50 });
      if (res.ok) setOccurrences(res.value.occurrences); else fail(res.error);
    } else if (tab === 'ratings' && authority.mayViewRelationships) {
      setRatings(undefined);
      const res = await runtime.reputation.recentRatings({ marketId: market, maxStars, limit: 50 });
      if (res.ok) setRatings(res.value.ratings); else fail(res.error);
    } else if (tab === 'blocks' && authority.mayReadOccurrences) {
      setBlocks(undefined);
      const res = await runtime.reputation.blocks({ country, ...(blockReason !== '' ? { reason: blockReason } : {}), limit: 50 });
      if (res.ok) setBlocks(res.value.blocks); else fail(res.error);
    }
  }, [runtime, market, country, tab, status, maxStars, blockReason, authority.mayReadOccurrences, authority.mayViewRelationships, fail]);

  useEffect(() => { void loadOverview(); void loadReasons(); }, [loadOverview, loadReasons]);
  useEffect(() => { void loadTab(); }, [loadTab]);

  const unblockActorName = usePersonName(unblocking?.actorId);
  const drawerOpen = (tab === 'occurrences' && selected !== undefined) || (tab === 'blocks' && authority.mayUnblock && unblocking !== undefined);

  const refreshAll = async (): Promise<void> => { await loadOverview(); await loadTab(); };

  const respond = async (): Promise<void> => {
    if (selected === undefined) return;
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    const res = await runtime.reputation.answerOccurrence(selected.marketId, selected.occurrenceId, answer);
    setBusy(false);
    if (!res.ok) { fail(res.error); return; }
    setSelected(res.value.occurrence);
    setAnswer('');
    setDone(t('rep.rel.occ.answered'));
    await refreshAll();
  };

  const close = async (): Promise<void> => {
    if (selected === undefined) return;
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    const res = await runtime.reputation.closeOccurrence(selected.marketId, selected.occurrenceId);
    setBusy(false);
    if (!res.ok) { fail(res.error); return; }
    setSelected(res.value.occurrence);
    setDone(t('rep.rel.occ.closed'));
    await refreshAll();
  };

  // Q-136 (H1-RULES D503): a block never expires — only the blocker or this audited action (word `relationship:unblock`, a written reason) undoes it.
  const unblock = async (): Promise<void> => {
    if (unblocking === undefined) return;
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    const res = await runtime.reputation.unblock(unblocking.id, unblockReason.trim());
    setBusy(false);
    if (!res.ok) { fail(res.error); return; }
    setUnblocking(undefined);
    setUnblockReason('');
    setDone(t('rep.rel.blocks.unblocked'));
    await refreshAll();
  };

  const saveReason = async (): Promise<void> => {
    if (country === undefined) return;
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    const labels: Record<string, string> = {};
    if (draft.pt.trim() !== '') labels.pt = draft.pt.trim();
    if (draft.en.trim() !== '') labels.en = draft.en.trim();
    const res = await runtime.reputation.saveOccurrenceReason({ countryCode: country, code: draft.code.trim(), audience: draft.audience, labels, active: draft.active, sortOrder: Number.parseInt(draft.order, 10) || 100 });
    setBusy(false);
    if (!res.ok) { fail(res.error); return; }
    setDone(t('rep.rel.reasons.saved'));
    setDraft({ code: '', audience: 'BOTH', pt: '', en: '', order: '100', active: true });
    await loadReasons();
  };

  const when = (iso: string): string => formatMarketInstant(iso, tz, lang);
  const counters = overview;

  return (
    <>
      <PageHeader title={t('rep.rel.title')} subtitle={t('rep.rel.subtitle')} />

      <Panel>
        <Stack gap={4}>
          <FormRow
            columns="minmax(0, 360px)"
            action={
              <SegmentedControl
                ariaLabel={t('rep.rel.tabs')}
                value={tab}
                onChange={(v: Tab) => { setTab(v); setSelected(undefined); }}
                options={[
                  { id: 'occurrences', label: t('rep.rel.tab.occurrences') },
                  { id: 'ratings', label: t('rep.rel.tab.ratings') },
                  // D502 (Q-137): the blacklist names who blocked whom — read with `occurrence:read` (the server re-authorizes), not the `*:view` wildcard.
                  ...(authority.mayReadOccurrences ? [{ id: 'blocks' as const, label: t('rep.rel.tab.blocks') }] : []),
                  { id: 'reasons', label: t('rep.rel.tab.reasons') },
                ]}
              />
            }
          >
            <MarketField id="rep-rel-market" value={market} onChange={setMarket} />
          </FormRow>
          {market === '' && <Notice tone="neutral">{t('rep.marketHint')}</Notice>}
          {counters !== undefined && (
            <CounterRow aria-label={t('rep.rel.counters')}>
              {counters.occurrences !== null && (['NEW', 'ANSWERED', 'CLOSED'] as const).map((s) => (
                <div key={s}>
                  <Text as="dt" role="caption" tone="tertiary">{t(`rep.rel.counter.${s}`)}</Text>
                  <Text as="dd" role="title" mono>{counters.occurrences?.[s] ?? 0}</Text>
                </div>
              ))}
              <div><Text as="dt" role="caption" tone="tertiary">{t('rep.rel.counter.blocks')}</Text><Text as="dd" role="title" mono>{counters.relationships.blocks}</Text></div>
              <div><Text as="dt" role="caption" tone="tertiary">{t('rep.rel.counter.safetyBlocks')}</Text><Text as="dd" role="title" mono>{counters.relationships.safetyBlocks}</Text></div>
              <div><Text as="dt" role="caption" tone="tertiary">{t('rep.rel.counter.favorites')}</Text><Text as="dd" role="title" mono>{counters.relationships.favorites}</Text></div>
            </CounterRow>
          )}
          {error !== undefined && !drawerOpen && <Notice tone="danger">{error}</Notice>}
          {done !== undefined && !drawerOpen && <Notice tone="success">{done}</Notice>}
        </Stack>
      </Panel>

      {tab === 'occurrences' && (
        <section aria-label={t('rep.rel.tab.occurrences')}>
          <Panel>
            <Stack gap={4}>
              <Field label={t('rep.rel.occ.status')} htmlFor="rep-rel-status">
                <Select id="rep-rel-status" value={status} onChange={(e) => setStatus(e.target.value as '' | OccurrenceStatusView)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s === '' ? t('rep.rel.occ.status.all') : v('reputation.occurrence_status', s)}</option>)}
                </Select>
              </Field>
            </Stack>
          </Panel>
          <Panel padded={false}>
            <Table
              columns={[
                { key: 'at', header: t('rep.rel.occ.col.at'), render: (o: OccurrenceView) => <span className="tnum">{when(o.createdAt)}</span> },
                { key: 'who', header: t('rep.rel.occ.col.who'), render: (o: OccurrenceView) => <span>{v('identity.realm', o.reporterRole)}</span> },
                { key: 'reason', header: t('rep.rel.occ.col.reason'), render: (o: OccurrenceView) => <span>{occurrenceReasonLabel(reasons, o.reasonCode, lang, uncatalogued)}</span> },
                { key: 'text', header: t('rep.rel.occ.col.text'), render: (o: OccurrenceView) => <span>{o.text.length > 80 ? `${o.text.slice(0, 80)}…` : o.text}</span> },
                { key: 'status', header: t('rep.rel.occ.col.status'), render: (o: OccurrenceView) => <Badge tone={OCCURRENCE_STATUS_TONE[o.status]}>{v('reputation.occurrence_status', o.status)}</Badge> },
              ]}
              rows={occurrences ?? []}
              rowKey={(o) => o.occurrenceId}
              onRowClick={(o) => { setSelected(o); setAnswer(''); setDone(undefined); }}
              {...(selected !== undefined ? { selectedKey: selected.occurrenceId } : {})}
              loading={market !== '' && occurrences === undefined && error === undefined}
              loadingLabel={t('ui.loading')}
              error={error !== undefined ? t('ui.loadFailed') : undefined}
              onRetry={() => void loadTab()}
              retryLabel={t('ui.retry')}
              empty={<EmptyState title={market === '' ? t('rep.marketHint') : t('rep.rel.occ.empty')} />}
            />
          </Panel>
          <Drawer open={tab === 'occurrences' && selected !== undefined} onClose={() => setSelected(undefined)} labelledBy="rep-rel-occ-title">
            {selected !== undefined && (
              <>
                <DrawerHeader titleId="rep-rel-occ-title" title={occurrenceReasonLabel(reasons, selected.reasonCode, lang, uncatalogued)} subtitle={<Badge tone={OCCURRENCE_STATUS_TONE[selected.status]}>{v('reputation.occurrence_status', selected.status)}</Badge>} onClose={() => setSelected(undefined)} closeLabel={t('ui.close')} />
                <DrawerBody>
                  <DrawerStack>
                    {error !== undefined && <Notice tone="danger">{error}</Notice>}
                    {done !== undefined && <Notice tone="success">{done}</Notice>}
                    <Text role="caption" tone="tertiary" block>{t('rep.rel.occ.detail', { at: when(selected.createdAt), role: v('identity.realm', selected.reporterRole) })}</Text>
                    <PreWrap>{selected.text}</PreWrap>
                    {selected.answer !== undefined && (
                      <Notice tone="success">
                        <strong>{t('rep.rel.occ.answer')}</strong> {selected.answer}
                      </Notice>
                    )}
                    {authority.mayRespond && selected.status === 'NEW' && (
                      <Field label={t('rep.rel.occ.answer.label')} htmlFor="rep-rel-answer" hint={t('rep.rel.occ.answer.hint')}>
                        <TextArea id="rep-rel-answer" rows={3} maxLength={2000} value={answer} onChange={(e) => setAnswer(e.target.value)} />
                      </Field>
                    )}
                  </DrawerStack>
                </DrawerBody>
                <DrawerFooter>
                  {authority.mayRespond && selected.status === 'NEW' && (
                    <>
                      <Button variant="secondary" disabled={busy} onClick={() => void close()}>{t('rep.rel.occ.close')}</Button>
                      <Button variant="primary" disabled={busy || answer.trim() === ''} onClick={() => void respond()}>{t('rep.rel.occ.respond')}</Button>
                    </>
                  )}
                  {authority.mayRespond && selected.status === 'ANSWERED' && (
                    <Button variant="secondary" disabled={busy} onClick={() => void close()}>{t('rep.rel.occ.close')}</Button>
                  )}
                </DrawerFooter>
              </>
            )}
          </Drawer>
        </section>
      )}

      {tab === 'ratings' && (
        <section aria-label={t('rep.rel.tab.ratings')}>
          <Panel>
            <Stack gap={4}>
              <Field label={t('rep.rel.ratings.max')} htmlFor="rep-rel-stars" hint={t('rep.rel.ratings.hint')}>
                <Select id="rep-rel-stars" value={String(maxStars)} onChange={(e) => setMaxStars(Number.parseInt(e.target.value, 10))}>
                  {[1, 2, 3].map((n) => <option key={n} value={String(n)}>{t('rep.rel.ratings.upTo', { n: String(n) })}</option>)}
                </Select>
              </Field>
            </Stack>
          </Panel>
          <Panel padded={false}>
            <Table
              columns={[
                { key: 'at', header: t('rep.rel.occ.col.at'), render: (r: RecentRatingView) => <span className="tnum">{when(r.createdAt)}</span> },
                { key: 'who', header: t('rep.rel.occ.col.who'), render: (r: RecentRatingView) => <span>{v('identity.realm', r.raterRole === 'rider' ? 'RIDER' : 'DRIVER')}</span> },
                { key: 'stars', header: t('rep.rel.ratings.col.stars'), render: (r: RecentRatingView) => <span className="tnum">{r.stars}</span> },
                { key: 'tags', header: t('rep.rel.ratings.col.tags'), render: (r: RecentRatingView) => <span>{r.tags.map((tag) => v('ride.rating_tag', tag)).join(', ') || '—'}</span> },
                { key: 'note', header: t('rep.rel.ratings.col.note'), render: (r: RecentRatingView) => <span>{r.note ?? '—'}</span> },
              ]}
              rows={ratings ?? []}
              rowKey={(r) => `${r.rideId}:${r.raterRole}`}
              loading={market !== '' && ratings === undefined && error === undefined}
              loadingLabel={t('ui.loading')}
              error={error !== undefined ? t('ui.loadFailed') : undefined}
              onRetry={() => void loadTab()}
              retryLabel={t('ui.retry')}
              empty={<EmptyState title={market === '' ? t('rep.marketHint') : t('rep.rel.ratings.empty')} />}
            />
          </Panel>
        </section>
      )}

      {tab === 'blocks' && (
        <section aria-label={t('rep.rel.tab.blocks')}>
          <Panel>
            <Stack gap={4}>
              <Field label={t('rep.rel.blocks.reason')} htmlFor="rep-rel-block-reason">
                <Select id="rep-rel-block-reason" value={blockReason} onChange={(e) => setBlockReason(e.target.value as '' | 'safety' | 'preference')}>
                  <option value="">{t('rep.rel.blocks.reason.all')}</option>
                  <option value="safety">{v('relationships.block_reason', 'safety')}</option>
                  <option value="preference">{v('relationships.block_reason', 'preference')}</option>
                </Select>
              </Field>
            </Stack>
          </Panel>
          <Panel padded={false}>
            <Table
              columns={[
                { key: 'at', header: t('rep.rel.occ.col.at'), render: (b: BlockItemView) => <span className="tnum">{when(b.createdAt)}</span> },
                { key: 'actor', header: t('rep.rel.blocks.col.actor'), render: (b: BlockItemView) => <span>{b.actorName ?? <PersonName id={b.actorId} />}<Text role="caption" tone="tertiary" block>{v('identity.realm', b.actorRole === 'rider' ? 'RIDER' : 'DRIVER')}</Text></span> },
                { key: 'subject', header: t('rep.rel.blocks.col.subject'), render: (b: BlockItemView) => <span>{b.subjectName ?? <PersonName id={b.subjectId} />}</span> },
                { key: 'reason', header: t('rep.rel.blocks.col.reason'), render: (b: BlockItemView) => <Badge tone={b.blockReason === 'safety' ? 'danger' : 'neutral'}>{v('relationships.block_reason', b.blockReason)}</Badge> },
                ...(authority.mayUnblock
                  ? [{ key: 'unblock', header: '', render: (b: BlockItemView) => <Button variant="secondary" disabled={busy} onClick={() => { setUnblocking(b); setUnblockReason(''); setError(undefined); setDone(undefined); }}>{t('rep.rel.blocks.unblock')}</Button> }]
                  : []),
              ]}
              rows={blocks ?? []}
              rowKey={(b) => b.id}
              loading={market !== '' && blocks === undefined && error === undefined}
              loadingLabel={t('ui.loading')}
              error={error !== undefined ? t('ui.loadFailed') : undefined}
              onRetry={() => void loadTab()}
              retryLabel={t('ui.retry')}
              empty={<EmptyState title={market === '' ? t('rep.marketHint') : t('rep.rel.blocks.empty')} />}
            />
          </Panel>
          <Drawer open={authority.mayUnblock && unblocking !== undefined} onClose={() => { setUnblocking(undefined); setUnblockReason(''); }} labelledBy="rep-rel-unblock-title">
            {authority.mayUnblock && unblocking !== undefined && (
              <>
                <DrawerHeader titleId="rep-rel-unblock-title" title={t('rep.rel.blocks.unblock.title', { who: unblocking.actorName ?? unblockActorName ?? tailOf(unblocking.actorId) })} onClose={() => { setUnblocking(undefined); setUnblockReason(''); }} closeLabel={t('ui.close')} />
                <DrawerBody>
                  <DrawerStack>
                    {error !== undefined && <Notice tone="danger">{error}</Notice>}
                    <Field label={t('rep.rel.blocks.unblock.reason')} htmlFor="rep-rel-unblock-reason" hint={t('rep.rel.blocks.unblock.hint')}>
                      <TextArea id="rep-rel-unblock-reason" rows={3} maxLength={300} value={unblockReason} onChange={(e) => setUnblockReason(e.target.value)} />
                    </Field>
                  </DrawerStack>
                </DrawerBody>
                <DrawerFooter>
                  <Button variant="secondary" disabled={busy} onClick={() => { setUnblocking(undefined); setUnblockReason(''); }}>{t('rep.rel.blocks.unblock.cancel')}</Button>
                  <Button variant="primary" disabled={busy || unblockReason.trim().length < 3} onClick={() => void unblock()}>{t('rep.rel.blocks.unblock.confirm')}</Button>
                </DrawerFooter>
              </>
            )}
          </Drawer>
        </section>
      )}

      {tab === 'reasons' && (
        <section aria-label={t('rep.rel.tab.reasons')}>
          <Panel padded={false}>
            <Table
              columns={[
                { key: 'code', header: t('rep.rel.reasons.col.code'), render: (r: OccurrenceReasonView) => <span className="tnum">{r.code}</span> },
                { key: 'labels', header: t('rep.rel.reasons.col.labels'), render: (r: OccurrenceReasonView) => <span>{Object.entries(r.labels).map(([l, x]) => `${language(l)}: ${x}`).join(' · ')}</span> },
                { key: 'audience', header: t('rep.rel.reasons.col.audience'), render: (r: OccurrenceReasonView) => <span>{v('occurrence.audience', r.audience)}</span> },
                { key: 'active', header: t('rep.rel.reasons.col.active'), render: (r: OccurrenceReasonView) => <Badge tone={r.active ? 'success' : 'neutral'}>{t(r.active ? 'rep.rel.reasons.active' : 'rep.rel.reasons.inactive')}</Badge> },
                { key: 'edit', header: '', render: (r: OccurrenceReasonView) => authority.mayManageReasons ? <Button size="sm" variant="secondary" onClick={() => setDraft({ code: r.code, audience: r.audience, pt: r.labels.pt ?? '', en: r.labels.en ?? '', order: String(r.sortOrder), active: r.active })}>{t('rep.rel.reasons.edit')}</Button> : <span /> },
              ]}
              rows={reasons}
              rowKey={(r) => r.code}
              empty={<EmptyState title={market === '' ? t('rep.marketHint') : t('rep.rel.reasons.empty')} />}
            />
          </Panel>
          {authority.mayManageReasons && country !== undefined && (
            <Panel>
              <Stack gap={4}>
                <SectionTitle>{t('rep.rel.reasons.form', { country })}</SectionTitle>
                <form onSubmit={(e) => { e.preventDefault(); void saveReason(); }}>
                  <Stack gap={4}>
                    <FormGrid>
                    <Field label={t('rep.rel.reasons.code')} htmlFor="rep-rel-code" hint={t('rep.rel.reasons.code.hint')}>
                      <TextInput id="rep-rel-code" value={draft.code} autoComplete="off" onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
                    </Field>
                    <Field label={t('rep.rel.reasons.audience')} htmlFor="rep-rel-audience">
                      <Select id="rep-rel-audience" value={draft.audience} onChange={(e) => setDraft({ ...draft, audience: e.target.value as OccurrenceAudienceView })}>
                        {AUDIENCES.map((a) => <option key={a} value={a}>{v('occurrence.audience', a)}</option>)}
                      </Select>
                    </Field>
                    <Field label={t('rep.rel.reasons.label.pt')} htmlFor="rep-rel-label-pt">
                      <TextInput id="rep-rel-label-pt" value={draft.pt} maxLength={80} onChange={(e) => setDraft({ ...draft, pt: e.target.value })} />
                    </Field>
                    <Field label={t('rep.rel.reasons.label.en')} htmlFor="rep-rel-label-en">
                      <TextInput id="rep-rel-label-en" value={draft.en} maxLength={80} onChange={(e) => setDraft({ ...draft, en: e.target.value })} />
                    </Field>
                    <Field label={t('rep.rel.reasons.order')} htmlFor="rep-rel-order">
                      <TextInput id="rep-rel-order" className="tnum" inputMode="numeric" value={draft.order} onChange={(e) => setDraft({ ...draft, order: e.target.value })} />
                    </Field>
                    </FormGrid>
                    <Cluster gap={4} wrap>
                      <CheckRow id="rep-rel-active" checked={draft.active} onChange={(v: boolean) => setDraft({ ...draft, active: v })}>{t('rep.rel.reasons.active')}</CheckRow>
                      <Button type="submit" variant="primary" disabled={busy || draft.code.trim() === '' || (draft.pt.trim() === '' && draft.en.trim() === '')}>{t('rep.rel.reasons.save')}</Button>
                    </Cluster>
                  </Stack>
                </form>
              </Stack>
            </Panel>
          )}
        </section>
      )}
    </>
  );
}
